'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isProduction } from '../services/env';
import mapFieldsToState from '../lib/mapFieldsToState';
import getVariantsFormFields from '../lib/getVariantsFormFields';
import { formatPriceInput } from '../services/formatPrice';
import getPaymentMethod from '../lib/getPaymentMethod';
import { useQueryClient } from '@tanstack/react-query';
import getSessionId from '../lib/getSessionId';
import { submissionKey, paymentKey } from '../queryKeys';
import { getTicketGroupsFormState } from '../lib/getTicketGroupsFormState';
import getCheckoutPath from '../lib/getCheckoutPath';
import getSellerSlug from '../services/getSellerSlug';
import getCheckoutSlug from '../services/getCheckoutSlug';
import getPaymentOption from '../lib/getPaymentOption';
import useCheckoutForm from './useCheckoutForm';
import useMatchMedia from './useMatchMedia';
import useCheckoutRouter from './useCheckoutRouter';
import { checkoutTypes } from '../enums/checkout-types';
import useDebouncedCallback from './useDebouncedCallback';
import api from '../api';
import { PAGE_TYPES } from '../enums/page-types';
import getFormFieldValues from '../lib/getFormFieldValues';
import getFormVariantValues from '../lib/getFormVariantValues';
import getTicketTypeValues from '../lib/getTicketTypeValues';

export const checkoutStates = {
  INITIAL: 'initial',
  SUBMITTING: 'submitting',
  CREATING_SUBMISSION: 'creating_submission',
  CREATING_PAYMENT: 'creating_payment',
  CREATING_SUBSCRIPTION: 'creating_subscription',
  PROCESSING: 'processing',
  PROCESSING_PAYMENT: 'processing_payment',
  PROCESSING_SUBSCRIPTION: 'processing_subscription',
  ERROR: 'error',
  PENDING: 'pending',
  COMPLETED: 'completed',
};

export const checkoutLayouts = {
  CENTER: 'center',
  SPLIT: 'split',
  DOUBLE: 'double',
  DOUBLE_BANNER: 'double_banner',
};

const DELAY_PREFILLED_DRAFT_CREATION_MS = 60000;

function getInitialCheckoutState(query) {
  const { payment_intent, payment_intent_client_secret } = query;

  if (payment_intent && payment_intent_client_secret) {
    return checkoutStates.PROCESSING_PAYMENT;
  }

  return checkoutStates.INITIAL;
}

function getAddressMode({ shippingAddressCollection, billingAddressCollection }) {
  if (shippingAddressCollection) {
    return 'shipping';
  } else if (billingAddressCollection) {
    return 'billing';
  } else {
    return null;
  }
}

function getCheckoutLayout(checkoutLayout, checkoutType) {
  if (checkoutType === checkoutTypes.EMBED) {
    return checkoutLayouts.CENTER;
  } else if (checkoutType === checkoutTypes.CHECKOUT || checkoutType === checkoutTypes.IFRAME) {
    if (checkoutLayout === checkoutLayouts.DOUBLE) {
      return checkoutLayouts.DOUBLE;
    } else {
      return checkoutLayout;
    }
  } else {
    return checkoutLayouts.CENTER;
  }
}

const CheckoutStateContext = React.createContext({});

const CheckoutStateProvider = ({ value = {}, children }) => {
  const {
    checkout = {},
    product = {},
    seller = {},
    options: options = {
      type: checkoutTypes.CHECKOUT,
    },
    upsell = {},
    isUpsell = false,
    isPreview = false,
    isTesting = false,
    isTestingPayments = false,
    isMeasurer = false,
    isScreenshot = false,
    query = {},
    params = {},
    countryCode,
    iframeId,
    isConfirmation = false,
    publicKey,
    publicKeyTest,
    draftCheckout = null,
  } = value;

  const checkoutType = useMemo(() => options.type, [options.type]);

  const isSmall = useMatchMedia('(max-width: 768px)');

  const checkoutLayout = useMemo(
    () => getCheckoutLayout(checkout.hostedLayoutType, checkoutType, isSmall),
    [checkout.hostedLayoutType, checkoutType, isSmall],
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [payment, setPayment] = useState(null);
  const [customer, setCustomer] = useState({});
  const [paymentGateway, setPaymentGateway] = useState(Object.keys(checkout.paymentMethods)[0]);
  const [paymentOption, setPaymentOption] = useState(getPaymentOption(checkout.paymentOptions));
  const [paymentMethod, setPaymentMethod] = useState(
    getPaymentMethod(checkout.paymentMethods, 'stripe'),
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [paymentError, setPaymentError] = useState('');
  const [formError, setFormError] = useState('');
  const [payWhatYouWantPrice, setPayWhatYouWantPrice] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [showCouponCodeField, setShowCouponCodeField] = useState(
    checkout.showCouponCodeFieldType === 'field' || !!query?.coupon || !!draftCheckout?.coupon, // if using type 'field', show field immediately or if coupon is present
  );

  const { form, setForm } = useCheckoutForm({
    checkout,
    product,
    query,
    countryCode,
    onPayWhatYouWantPriceChange: setPayWhatYouWantPrice,
    draftCheckout,
    isPreview,
  });

  const [address, setAddress] = useState({
    mode: getAddressMode({
      shippingAddressCollection: checkout.shippingAddressCollection,
      billingAddressCollection: checkout.billingAddressCollection,
    }),
    name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
  });

  const [visitId, setVisitId] = useState('');
  const [trackedEvents, setTrackedEvents] = useState([]);
  const [state, setState] = useState(getInitialCheckoutState(query));

  const [layoutId, setLayoutId] = useState(null);
  const [freeTrialDuration, setFreeTrialDuration] = useState(1);
  const [freeTrialDurationType, setFreeTrialDurationType] = useState('day');
  const [isChecked, setIsChecked] = useState(false);
  const [addressMode, setAddressMode] = useState(getAddressMode(checkout));
  const [orderNote, setOrderNote] = useState('');
  const [showPaymentOption, setShowPaymentOption] = useState(false);
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const [stripeAddress, setStripeAddress] = useState({});
  const hasFocusedCheckoutRef = useRef(false);
  const hasInitiatedPaymentRef = useRef(false);
  const isSubmittingDraftRef = useRef(false);
  const mountTimestampRef = useRef(Date.now());
  const pendingDraftTimeoutRef = useRef(null);

  const formRef = useRef(null);
  const queryClient = useQueryClient();
  const [isUpsellRetry, setIsUpsellRetry] = useState(false);
  const router = useCheckoutRouter();

  /**
   * We delete the checkoutEventId query parameter client side for simplicity.
   * During SSR in order to delete the query parameter we would need to do a redirect.
   * This has two disadvantages
   * 1. The redirect means we have to call the `getPages` endpoint twice (slow)
   * 2. Our routing is complex and knowing the exact path to redirect to isn't reliable because we have hosted pages, embeds and overlays.
   */
  useEffect(() => {
    if (!draftCheckout) {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkoutEventId');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [draftCheckout]);

  const dynamicPrice = useMemo(() => {
    if (checkout.allowDynamicPrice && query.price) {
      return Number(query.price);
    }

    return null;
  }, [checkout, query.price]);

  const dynamicPlanIterations = useMemo(() => {
    if (checkout.productType !== 'subscription') {
      return null;
    }

    if (checkout.allowDynamicPlanIterations && query.no_of_payments) {
      return Number(query.no_of_payments);
    }

    return null;
  }, [checkout, query.no_of_payments]);

  useMemo(() => {
    setPayWhatYouWantPrice(formatPriceInput(form.payWhatYouWant, product.currency));
  }, [form.payWhatYouWant]);

  // update showCouponCodeField if changed
  useEffect(() => {
    setShowCouponCodeField(
      checkout.showCouponCodeFieldType === 'field' ||
        !!query?.coupon ||
        !!draftCheckout?.coupon ||
        !!coupon,
    );
  }, [checkout.showCouponCodeFieldType, query?.coupon, draftCheckout?.coupon, coupon]);

  // update paymentOption if changed
  useEffect(() => {
    if (!checkout.paymentOptions) {
      return;
    }

    const selectedPaymentOptionExists = !!checkout.paymentOptions.find(
      option => option.enabled === true && option._id === paymentOption._id,
    );

    if (paymentOption === null) {
      // if paymentOption is null, set the first enabled payment option
      setPaymentOption(getPaymentOption(checkout.paymentOptions));
    } else if (!selectedPaymentOptionExists) {
      // if the selected payment option no longer exists, set the first enabled payment option
      setPaymentOption(getPaymentOption(checkout.paymentOptions));
    } else {
      // set the currently selected payment option based on new data
      const updatedPaymentOption = checkout.paymentOptions.find(
        option => option._id === paymentOption._id,
      );

      setPaymentOption(updatedPaymentOption);
    }
  }, [checkout.paymentOptions]);

  useEffect(() => {
    if (!visitId) {
      return;
    }

    updateDraftChargeDebounced();
  }, [visitId]);

  useEffect(() => {
    return () => {
      if (pendingDraftTimeoutRef.current) {
        clearTimeout(pendingDraftTimeoutRef.current);
      }
    };
  }, []);

  async function handleSetSubmission(submission) {
    setSubmission(submission);
    setCustomer(submission.customer);

    const sessionId = getSessionId();

    // Update the submission in the query cache
    queryClient.setQueryData(submissionKey(submission._id, sessionId), submission);
  }

  function handleSetPayment(payment) {
    // Update the payment in the state
    setPayment(payment);
    setCustomer(payment.customer);

    const sessionId = getSessionId();

    // Update the payment in the query cache
    queryClient.setQueryData(paymentKey(payment._id, sessionId), payment);
  }

  function resetForm() {
    setForm({
      name: '',
      email: '',
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: '',
    });
    setPayWhatYouWantPrice(null);
    setCoupon(null);
    setPayment(null);
    setIsProcessing(false);
    setPaymentError('');
    setFormError('');
    setPaymentGateway(Object.keys(checkout.paymentMethods)[0]);
    setPaymentMethod(getPaymentMethod(checkout.paymentMethods, 'stripe'));
    setTrackedEvents([]);
    setState(checkoutStates.INITIAL);
  }

  function removeCoupon() {
    setCoupon(null);
  }

  function restartPayment({ isConfirmation = false } = {}) {
    // same as resetForm except we keep the paymentIntent
    // which is needed for redirect based payment methods
    // if the payment fails and checkout gets reloaded, it will have a new payment intent created on server
    // if we then use resetForm (on clicking Try again), the payment intent is removed from the checkout
    // and the purchase creates a new payment intent somehow, which results in orphan purchases
    // @todo we can combine this again once we launch payment element without payment intents

    setForm({
      name: '',
      email: '',
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: '',
    });
    setPayWhatYouWantPrice(null);
    setCoupon(null);
    setPayment(null);
    setIsProcessing(false);
    setPaymentError('');
    setFormError('');
    setPaymentGateway(Object.keys(checkout.paymentMethods)[0]);
    setPaymentMethod(getPaymentMethod(checkout.paymentMethods, 'stripe'));
    setTrackedEvents([]);
    setState(checkoutStates.INITIAL);

    // navigate back from confirmation to the checkout/form page
    if (isConfirmation) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);

      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }

  function getDraftPaymentData() {
    const { fields, name, email } = getFormFieldValues(form.fields, checkout.fields);
    const variants = getFormVariantValues(product.variants, form.variants);
    const tickets = getTicketTypeValues(checkout.ticketGroups, form.ticketGroups);
    const url = new URL(window.location.href);
    const checkoutEventId = url.searchParams.get('checkoutEventId');

    /**
     * Check for all fields that are an email element for their references. Check if the query
     * Has the email reference and a value. We do not validate if the email is valid.
     * This still shows intent of being prefilled and even with validation the API might not match.
     */
    const emailFields = (fields ?? []).filter(field => field.element === 'email');
    const emailFieldReferences = emailFields.map(field => field.reference);
    const queryHasEmail = emailFieldReferences.some(
      reference => reference != null && query[reference] != null,
    );
    const isPrefilled = draftCheckout?.isPrefilled === true || queryHasEmail === true;

    const hasInteraction =
      draftCheckout?.hasInteraction === true || hasFocusedCheckoutRef.current === true;

    return {
      ...(checkoutEventId && { checkoutEventId }),
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      variants,
      tickets,
      sellerId: seller._id,
      livemode: !isTesting,
      queryParameters: query,
      session: getSessionId(),
      visit: visitId,
      couponId: coupon?._id || null,
      selectedPaymentMethod: selectedPaymentMethod,
      paymentOption: paymentOption,
      ...(isUpsell && {
        upsell: true,
        upsellRetry: isUpsellRetry,
        upsellPayment: upsell?.initialPaymentId,
        upsellSubscription: upsell?.initialSubscriptionId,
        upsellCheckout: upsell?.initialCheckoutId,
        upsellCustomer: upsell?.customerId,
      }),
      ...(payWhatYouWantPrice && { payWhatYouWantPrice }),
      ...(dynamicPrice && { dynamicPrice }),
      ...(hasInteraction && { hasInteraction }),
      ...(isPrefilled && { isPrefilled }),
    };
  }

  /**
   * updateDraftCharge is defined here in useCheckoutState
   * to ensure a single debounced instance is shared across all components.
   * This prevents duplicate draft creation when multiple components trigger updates.
   */
  async function updateDraftCharge() {
    if (hasInitiatedPaymentRef.current === true) {
      return null;
    }

    if (isSubmittingDraftRef.current) {
      return null;
    }

    const paymentData = getDraftPaymentData();

    /**
     * This logic slows down the initial creation of a prefilled draft to 60 seconds.
     * If isPrefilled is true and hasInteraction is not true an update is scheduled for the next 60s.
     *
     * If hasInteraction is true we clear pendingDraftTimeoutRef and set the ref back to null.
     *
     *
     * We have added this change to combat bots visiting checkouts with query params prefilled.
     * When a bot did this, it created a draft instantly and never interacted
     * With the page further.
     */
    const elapsedTime = Date.now() - mountTimestampRef.current;
    if (
      paymentData.isPrefilled === true &&
      paymentData.hasInteraction !== true &&
      elapsedTime < DELAY_PREFILLED_DRAFT_CREATION_MS
    ) {
      if (!pendingDraftTimeoutRef.current) {
        const remainingTime = DELAY_PREFILLED_DRAFT_CREATION_MS - elapsedTime;
        pendingDraftTimeoutRef.current = setTimeout(() => {
          pendingDraftTimeoutRef.current = null;
          updateDraftChargeDebounced();
        }, remainingTime);
      }

      return null;
    } else {
      if (pendingDraftTimeoutRef.current) {
        clearTimeout(pendingDraftTimeoutRef.current);
        pendingDraftTimeoutRef.current = null;
      }
    }

    isSubmittingDraftRef.current = true;

    // Don't fire draft charge in preview mode - check multiple ways
    const isInPreviewMode = isPreview || query?.preview === 'true' || query?.preview === true;
    const isForm = checkout.type === PAGE_TYPES.FORM;

    /**
     * Prevent the creation or updating of a draft if the checkout is
     * currently submitting, creating, processing or completed a payment.
     * This prevents an issue where the draft changes the windows history causing a payment to hang on the
     * payment page despite having been paid/completed in some capacity.
     */
    const isSubmittingOrProcessing =
      state === checkoutStates.SUBMITTING ||
      state === checkoutStates.CREATING_PAYMENT ||
      state === checkoutStates.CREATING_SUBSCRIPTION ||
      state === checkoutStates.CREATING_SUBMISSION ||
      state === checkoutStates.PROCESSING_PAYMENT ||
      state === checkoutStates.COMPLETED;

    if (isInPreviewMode || isForm || isSubmittingOrProcessing || isUpsell || isTesting) {
      isSubmittingDraftRef.current = false;
      return null;
    }

    // Only fire API call if there's a valid email field with a valid email address
    const emailField = paymentData.fields?.find(field => field.type === 'email' && field.value);
    const isValidEmail = emailField && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailField.value);

    if (!isValidEmail) {
      isSubmittingDraftRef.current = false;
      return null;
    }

    try {
      const draftChargeEvent = await api.createOrUpdateCheckoutEvent(
        checkout._id,
        paymentData,
        paymentData.checkoutEventId || null,
      );

      if (draftChargeEvent?.data?._id) {
        const url = new URL(window.location.href);
        url.searchParams.set('checkoutEventId', draftChargeEvent.data._id);
        window.history.replaceState({}, '', url.pathname + url.search);
      }

      return draftChargeEvent;
    } finally {
      isSubmittingDraftRef.current = false;
    }
  }

  const [updateDraftChargeDebounced, cancelUpdateDraftCharge] = useDebouncedCallback(
    updateDraftCharge,
    1000,
  );

  const checkoutState = {
    page: checkout,
    checkout,
    product,
    seller,
    dynamicPrice,
    dynamicPlanIterations,
    isProcessing,
    setIsProcessing,
    submission,
    setSubmission: handleSetSubmission,
    payment,
    setPayment: handleSetPayment,
    paymentGateway,
    setPaymentGateway,
    paymentOption,
    setPaymentOption,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentError,
    setPaymentError,
    formError,
    setFormError,
    payWhatYouWantPrice,
    setPayWhatYouWantPrice,
    coupon,
    setCoupon,
    removeCoupon,
    form,
    setForm,
    address,
    setAddress,
    checkoutType,
    upsell,
    isUpsell,
    isUpsellRetry,
    setIsUpsellRetry,
    isTesting,
    isTestingPayments,
    isPreview,
    isMeasurer,
    visitId,
    setVisitId,
    trackedEvents,
    setTrackedEvents,
    state,
    setState,
    customer,
    setCustomer,
    query,
    params,
    resetForm,
    restartPayment,
    layoutId,
    setLayoutId,
    freeTrialDuration,
    setFreeTrialDuration,
    freeTrialDurationType,
    setFreeTrialDurationType,
    isChecked,
    setIsChecked,
    addressMode,
    setAddressMode,
    showCouponCodeField,
    setShowCouponCodeField,
    orderNote,
    setOrderNote,
    showPaymentOption,
    setShowPaymentOption,
    activeSidePanel,
    setActiveSidePanel,
    stripeAddress,
    setStripeAddress,
    hasFocusedCheckoutRef,
    hasInitiatedPaymentRef,
    formRef,
    isScreenshot,
    countryCode,
    iframeId,
    checkoutLayout,
    isConfirmation,
    publicKey,
    publicKeyTest,
    draftCheckout,
    updateDraftCharge,
    updateDraftChargeDebounced,
    cancelUpdateDraftCharge,
  };

  // only attach to window on client
  if (!isProduction && typeof window !== 'undefined') {
    window.checkoutState = checkoutState;
  }

  return (
    <CheckoutStateContext.Provider value={checkoutState}>{children}</CheckoutStateContext.Provider>
  );
};

export default function useCheckoutState() {
  const context = React.useContext(CheckoutStateContext);

  if (context === undefined) {
    throw new Error('useCheckoutState must be used within a CheckoutStateProvider');
  }

  return context;
}

export { CheckoutStateContext, CheckoutStateProvider };
